<template>
  <div class="task-card-wrap">
   <div class="task-row" :class="['urgency-' + task.urgency, { done: task.status === 'done' }]">
    <span class="row-tick"></span>

    <div class="row-main">
      <div class="row-title">{{ task.title }}</div>
      <div class="row-meta">
        <span v-if="task.project_name" class="proj" :style="{ color: task.project_color }">{{ task.project_name }}</span>
        <span v-if="task.project_name" class="sep">·</span>
        <span>{{ t('urgency.' + task.urgency) }}</span>
        <template v-if="task.visibility === 'private'">
          <span class="sep">·</span>
          <span class="priv">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            {{ t('taskCard.private') }}
          </span>
        </template>
        <template v-if="task.due_at">
          <span class="sep">·</span>
          <span :class="{ overdue: isOverdue }">{{ formatDate(task.due_at) }}</span>
        </template>
      </div>
    </div>

    <span :class="['badge', 'badge-' + task.status]">{{ statusLabel }}</span>

    <span v-if="task.assignee_name" class="row-ini" :title="task.assignee_name">{{ assigneeInitials }}</span>

    <div class="row-actions">
      <TaskTimer
        v-if="!isExternal"
        :task-id="task.id"
        :timer-seconds="task.timer_seconds"
        :timer-started-at="task.timer_started_at"
        @update="onTimerUpdate"
      />
      <button v-if="!isExternal" class="btn btn-sm btn-icon" :title="t('common.edit')" @click.stop="$emit('edit', task)">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
      </button>
      <button
        v-if="task.status !== 'done'"
        class="btn btn-sm btn-icon"
        :title="t('taskCard.markDone')"
        @click.stop="$emit('complete', task)"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
      </button>
      <button
        v-else-if="isInternal"
        class="btn btn-sm btn-icon"
        :title="t('taskCard.reopen')"
        @click.stop="$emit('reopen', task)"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
      </button>
      <button v-if="!isExternal" class="btn btn-sm btn-icon danger-icon" :title="t('taskCard.archive')" @click.stop="$emit('archive', task)">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
      </button>
      <button class="btn btn-sm btn-icon expand-btn" :class="{ 'is-open': expanded }" :title="t('subtasks.toggle')" @click.stop="expanded = !expanded">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
    </div>
   </div>

   <template v-if="expanded">
     <TaskSubtasks :task-id="task.id" :read-only="isExternal" />
     <TaskComments :task-id="task.id" />
   </template>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import TaskTimer from '@/components/TaskTimer.vue'
import TaskSubtasks from '@/components/TaskSubtasks.vue'
import TaskComments from '@/components/TaskComments.vue'
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'

const URGENCY_COLORS = { low: '#A8A192', normal: '#0F6E59', high: '#A66E08', critical: '#B8432B' }

export default {
  name: 'TaskCard',
  components: { TaskTimer, TaskSubtasks, TaskComments },
  props: { task: { type: Object, required: true } },
  emits: ['edit', 'complete', 'reopen', 'archive', 'timer-update'],
  setup(props, { emit }) {
    const { isManager: _isManager, isExternalClient, isInternal: _isInternal } = useAuth()
    const { t, locale } = useI18n()

    const expanded = ref(false)

    const isExternal   = computed(() => isExternalClient())
    const isManager    = computed(() => _isManager())
    const isInternal   = computed(() => _isInternal())
    const urgencyColor = computed(() => URGENCY_COLORS[props.task.urgency] || '#76B900')
    const statusLabel  = computed(() => t('status.' + props.task.status))
    const isOverdue    = computed(() => {
      if (!props.task.due_at || props.task.status === 'done') return false
      return new Date(props.task.due_at) < new Date()
    })
    const assigneeInitials = computed(() => {
      const n = props.task.assignee_name || ''
      return n.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    })

    function formatDate(dt) {
      if (!dt) return ''
      const loc = locale.value === 'he' ? 'he-IL' : 'en-GB'
      return new Date(dt).toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' })
    }

    function onTimerUpdate(payload) {
      emit('timer-update', { taskId: props.task.id, ...payload })
    }

    return { isExternal, isManager, isInternal, expanded, urgencyColor, statusLabel, isOverdue, formatDate, onTimerUpdate, t, assigneeInitials }
  },
}
</script>

<style scoped>
.task-card-wrap { display: flex; flex-direction: column; }
.expand-btn svg { transition: transform .15s; }
.expand-btn.is-open svg { transform: rotate(180deg); }

/* Dense task row — hairline-divided, flat. Urgency shown as an edge tick. */
.task-row {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0 14px 0 0;
  min-height: 54px;
  transition: background .12s, border-color .12s;
  position: relative;
  overflow: hidden;
}
.task-row:hover { background: var(--accent-wash); border-color: var(--border-2); }
.task-row.done  { opacity: .6; }

.row-tick { width: 3px; align-self: stretch; flex-shrink: 0; background: var(--low); }
.urgency-normal   .row-tick { background: var(--normal); }
.urgency-high     .row-tick { background: var(--high); }
.urgency-critical .row-tick { background: var(--critical); }

.row-main { flex: 1; min-width: 0; padding-inline-start: 12px; }
.row-title {
  font-size: 13.5px; font-weight: 500; line-height: 1.3;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.task-row.done .row-title { text-decoration: line-through; }
.row-meta {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--faint);
  margin-top: 2px;
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
}
.row-meta .sep { opacity: .6; }
.row-meta .proj { font-weight: 600; }
.row-meta .priv { display: inline-flex; align-items: center; gap: 3px; }
.row-meta .overdue { color: var(--danger); }

.row-ini {
  width: 26px; height: 26px; flex-shrink: 0;
  border-radius: 5px;
  background: var(--surface-2); border: 1px solid var(--border-2);
  color: var(--muted);
  display: grid; place-items: center;
  font-size: 10.5px; font-weight: 600;
}

.row-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.btn-icon {
  color: var(--muted);
  width: 28px; height: 28px;
  padding: 0;
  justify-content: center;
}
.btn-icon svg { width: 14px; height: 14px; stroke: currentColor; }
.btn-icon:hover { color: var(--text); background: var(--hover); }
.danger-icon:hover { color: var(--danger); background: color-mix(in srgb, var(--danger) 12%, transparent); }

/* Mobile: let the row wrap so the title gets a full line instead of truncating.
   Urgency moves from the side tick to a colored inline-start border. */
@media (max-width: 768px) {
  .task-row { flex-wrap: wrap; min-height: 0; padding: 11px 13px; gap: 8px 10px; border-inline-start: 3px solid var(--low); }
  .task-row.urgency-normal   { border-inline-start-color: var(--normal); }
  .task-row.urgency-high     { border-inline-start-color: var(--high); }
  .task-row.urgency-critical { border-inline-start-color: var(--critical); }
  .row-tick { display: none; }
  .row-main { flex: 1 1 100%; padding-inline-start: 0; }
  .row-title { white-space: normal; overflow: visible; }
  .row-actions { margin-inline-start: auto; }
}
@media (max-width: 560px) {
  .row-actions { gap: 2px; }
  .row-meta { font-size: 10px; }
}
</style>
